// src/components/myteconstruction/Onboarding/OnboardingFlow.js
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import ThreeSphere from "../../AMyteSphere/ThreeSphere";
import { CheckCircle } from "lucide-react";

function OnboardingFlow() {
  const { auth, completeOnboarding } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    companyName: "",
    website: "",
    hqAddress: "",
    hqOfficeName: "",
  });

  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setStatusMessage("Saving organization info...");

    try {
      const resp = await fetch("/api/myteconstruction/organization", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(formData),
      });
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error || "Failed to save organization info.");
      }

      const resp2 = await fetch("/api/myteconstruction/auth/complete-onboarding", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });
      if (!resp2.ok) {
        const errData2 = await resp2.json();
        throw new Error(errData2.error || "Failed to mark onboarding complete.");
      }

      completeOnboarding();
      setStatusMessage("Onboarding complete!");
      setSuccess(true);

      setTimeout(() => {
        navigate("/myteconstruction/dashboard");
      }, 1500);
    } catch (error) {
      setStatusMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen relative bg-black flex items-center justify-center px-4 text-white overflow-hidden">
      {/* Background Sphere */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ThreeSphere zoomMultiplier={3.2} />
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate("/myteconstruction/dashboard")}
        className="absolute top-4 right-4 bg-gray-800 text-sm px-3 py-1 rounded-md hover:bg-gray-700 z-20"
      >
        Back to Dashboard
      </button>

      {/* Card */}
      <div className="relative z-10 max-w-md w-full bg-gray-900 bg-opacity-90 p-6 sm:p-8 rounded-lg shadow-lg">
        {!success ? (
          <>
            <div className="text-center mb-6">
              <img src="/LogoFile.png" alt="Myte Construction Logo" className="mx-auto w-24 mb-4" />
              <h2 className="text-2xl font-bold">Complete Your Organization Setup</h2>
              <p className="text-blue-300 text-sm mt-1">This helps us tailor your experience.</p>
            </div>

            {statusMessage && (
              <div className="text-center text-sm mb-4 text-blue-300">
                {statusMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {["companyName", "website", "hqAddress", "hqOfficeName"].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-semibold capitalize mb-1">
                    {field.replace(/([A-Z])/g, " $1")}
                  </label>
                  <input
                    type={field === "website" ? "url" : "text"}
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    className="w-full p-2 rounded-md bg-gray-800 text-white focus:outline-none"
                    required={field === "companyName"}
                  />
                </div>
              ))}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-white font-semibold"
              >
                {loading ? "Saving..." : "Complete Onboarding"}
              </button>
            </form>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center">
            <CheckCircle className="w-14 h-14 text-green-400 animate-pulse" />
            <h3 className="text-xl font-bold mt-4 text-white">You're all set!</h3>
            <p className="text-blue-200 text-sm mt-2">
              Redirecting to your dashboard...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default OnboardingFlow;
